import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import type { UploadUrlInput } from '@proref/schemas';
import {
  MAX_CLIP_MB,
  MAX_CLIP_MIN,
  describeFileProblem,
  formatDuration,
  titleFromFilename,
} from '../../lib/clip-file';
import { useAuth } from '../../contexts/auth-context';
import {
  useCreateUploadUrl,
  useUploadToS3,
  useClipStatusQuery,
  useDiscardDraftClip,
  useMarkUploadComplete,
  invalidateClipCaches,
} from '../../hooks/use-clips';
import { useFederationsQuery } from '../../hooks/use-federations';
import { useSportsQuery } from '../../hooks/use-sports';
import { Modal } from '../../components/shared/Modal';
import { Field } from '../../components/shared/Field';
import { Button } from '../../components/shared/Button';
import { Spinner } from '../../components/shared/Spinner';
import { Icon } from '../../components/shared/Icons';
import { notifyError, notifySuccess } from '../../lib/toast';
import { probeVideo, type VideoProbe } from '../../lib/video-probe';

interface UploadClipDialogProps {
  open: boolean;
  onClose: () => void;
}

// Sentinel for the super_admin federation picker meaning "Official (all federations)" — chosen, so
// it satisfies the "must pick something" guard, but it omits federation_id → a platform clip.
const OFFICIAL_VALUE = 'official';

// "Upload a clip" wizard — opened from the Clips page header. A deliberately small flow:
// choose a file → Upload → watch it transcode. There are no title/description fields anymore;
// the clip is named after the file and everything else is authored in the editor.
//
// The payoff is the hand-off: if the admin stays on the modal until MediaConvert finishes, we
// drop them straight into that clip's editor to tag + publish. If they'd rather not wait, "Leave"
// closes the modal and the draft keeps transcoding in the background (it's already in the list,
// which polls its status on its own).
export function UploadClipDialog({ open, onClose }: UploadClipDialogProps) {
  const { t } = useTranslation();
  const { role } = useAuth();
  const isSuper = role === 'super_admin';

  // super_admin uploads into an explicit federation+sport; a fed_admin's tenancy is JWT-derived,
  // so those pickers (and their catalogue fetches) are skipped for them.
  const federations = useFederationsQuery(isSuper);
  const sports = useSportsQuery(isSuper);

  const navigate = useNavigate();
  const qc = useQueryClient();
  const createUploadUrl = useCreateUploadUrl();
  const uploadFile = useUploadToS3();
  const discardDraft = useDiscardDraftClip();
  const markComplete = useMarkUploadComplete();

  const [file, setFile] = useState<File | null>(null);
  // Duration, read from the file in the browser. Probing on SELECT rather than on upload is
  // deliberate: the admin sees an over-long clip immediately, instead of after waiting out a
  // 500 MB PUT that was always going to be truncated.
  const [probe, setProbe] = useState<VideoProbe | null>(null);
  const [probing, setProbing] = useState(false);
  // The file the in-flight probe belongs to. Probing a 500 MB file takes long enough that an admin
  // can pick a second one first; without this the slower probe would overwrite the newer file's
  // metadata and the dialog would judge the wrong clip.
  const probeTargetRef = useRef<File | null>(null);
  const [federationId, setFederationId] = useState('');
  const [sportId, setSportId] = useState('');
  // Set once the presign + PUT succeed — we hold the new clip so we can poll it and, on Ready,
  // open its editor.
  const [clip, setClip] = useState<{ id: string; title: string } | null>(null);
  // S3 PUT finished → the server is now transcoding, so it's worth polling the status.
  const [uploaded, setUploaded] = useState(false);
  const [stage, setStage] = useState<'select' | 'working' | 'failed'>('select');

  // In flight = the presign call or the S3 PUT is running. During this window the modal can't be
  // dismissed (aborting a PUT mid-stream would orphan a half-written object).
  const isUploading = createUploadUrl.isPending || uploadFile.isPending;

  // Poll the clip's transcoding status only while the modal is open AND the bytes have landed
  // (before that the status is trivially 'uploading'). Gating on `open` means leaving stops the
  // poll immediately — the Clips list's own in-flight polling takes it from there.
  const statusQuery = useClipStatusQuery(clip?.id ?? null, open && uploaded);
  const serverStatus = statusQuery.data?.status;

  // Reset all state when the modal closes so a re-open starts clean — the component stays mounted
  // across open/close, so without this a second upload would resume the first one's stage.
  useEffect(() => {
    if (open) return;
    setFile(null);
    probeTargetRef.current = null;
    setProbe(null);
    setProbing(false);
    setFederationId('');
    setSportId('');
    setClip(null);
    setUploaded(false);
    setStage('select');
  }, [open]);

  // Terminal transitions off the polled status. Ready → hand off to the editor (close first so the
  // reset effect runs). Failed → surface the failure panel. Guarded on `open`/`clip` so a poll that
  // resolves just as the admin leaves can't yank them into the editor.
  useEffect(() => {
    if (!open || !clip) return;
    if (serverStatus === 'ready') {
      onClose();
      navigate(`/admin/clips/${clip.id}/edit`);
    } else if (serverStatus === 'failed') {
      setStage('failed');
    }
  }, [serverStatus, open, clip, onClose, navigate]);

  // Read the file's duration/fps, discarding the result if a newer file was picked meanwhile.
  async function selectFile(next: File | null) {
    probeTargetRef.current = next;
    setFile(next);
    setProbe(null);
    if (!next) {
      setProbing(false);
      return;
    }
    setProbing(true);
    const result = await probeVideo(next);
    // A superseded probe returns without touching state — the newer one owns `probing` and will
    // clear it when it finishes.
    if (probeTargetRef.current !== next) return;
    setProbe(result);
    setProbing(false);
  }

  async function startUpload() {
    if (!file) return;
    const problem = describeFileProblem(file, probe, t);
    if (problem) {
      notifyError(new Error('File rejected'), problem);
      return;
    }
    if (isSuper && (!federationId || !sportId)) {
      notifyError(new Error('Missing tenancy'), t('admin.clips.uploadDialog.missingTenancyToast'));
      return;
    }

    setStage('working');
    // The presign creates the draft row, so from that point on a failure has something to clean up.
    // Held in a local (not state) because setClip won't be readable in this same tick.
    let draftId: string | null = null;
    try {
      const payload: UploadUrlInput = {
        // Title defaults to the filename; the admin renames it in the editor after Ready.
        title: titleFromFilename(file.name, t),
        content_type: 'video/mp4',
        size_bytes: file.size,
        // super_admin names the sport, and EITHER a federation OR "Official" (the OFFICIAL_VALUE
        // sentinel) — Official omits federation_id, so the backend files it as a platform clip.
        ...(isSuper && {
          sport_id: sportId,
          ...(federationId !== OFFICIAL_VALUE && { federation_id: federationId }),
        }),
      };
      const { clip: created, upload_url } = await createUploadUrl.mutateAsync(payload);
      draftId = created.id;
      setClip({ id: created.id, title: created.title });
      await uploadFile.mutateAsync({ url: upload_url, file });
      // Bytes are in S3 and MediaConvert is already triggered by the S3 event — so move the clip
      // off "Uploading" for the transcode. Deliberately not awaited into the try's failure path:
      // the upload SUCCEEDED, and a failed status ping must not flip the wizard to its error
      // state. Worst case the chip reads "Uploading" until the webhook lands, as it did before.
      markComplete.mutate(draftId);
      // The draft row exists the moment the presign returns — surface it in the list right away so
      // the admin sees it transcoding even if they leave this modal. Sweeps every clip cache, not
      // just the admin table, so the referee library can't hold a stale view of the library either.
      invalidateClipCaches(qc);
      setUploaded(true);
    } catch (err: unknown) {
      // The row exists before the bytes do, so a failed PUT would leave a draft stuck in
      // `uploading` — it can never transcode (no object ever lands) and the clips list polls it
      // forever. Discard it so a failed upload leaves nothing behind, and so Try again starts
      // clean instead of stranding a second draft next to the first.
      if (draftId) {
        // Best-effort: the operator needs to see why the UPLOAD failed, not a cleanup error on
        // top of it. A draft that survives this stays visible in the clips list and can be
        // deleted by hand — there is no server-side sweep for stranded drafts yet.
        await discardDraft.mutateAsync(draftId).catch(() => {});
        setClip(null);
      }
      notifyError(err, t('admin.clips.uploadDialog.uploadFailedToast'));
      setStage('failed');
    }
  }

  // Leave / Cancel — just close. If a draft was already created it keeps transcoding and shows in
  // the Clips list; the toast reassures the admin their upload isn't lost.
  function leave() {
    if (uploaded) {
      notifySuccess(t('admin.clips.uploadDialog.stillTranscodingToast'));
    }
    onClose();
  }

  const fileProblem = file ? describeFileProblem(file, probe, t) : null;
  const canUpload =
    Boolean(file) &&
    !probing &&
    !fileProblem &&
    (!isSuper || (Boolean(federationId) && Boolean(sportId)));

  return (
    <Modal
      open={open}
      // Block dismiss while the PUT is streaming; otherwise a click-away/Escape leaves gracefully.
      onClose={isUploading ? () => {} : leave}
      title={t('admin.clips.uploadDialog.title')}
      sub={
        stage === 'failed'
          ? t('admin.clips.uploadDialog.subFailed')
          : stage === 'working'
            ? t('admin.clips.uploadDialog.subWorking')
            : t('admin.clips.uploadDialog.subSelect')
      }
      footer={<Footer stage={stage} isUploading={isUploading} canUpload={canUpload} onUpload={startUpload} onLeave={leave} onRetry={() => setStage('select')} />}
    >
      {stage === 'select' && (
        <div className="flex flex-col gap-3.5">
          {/* super_admin picks tenancy explicitly; a fed_admin's is JWT-derived and skipped. */}
          {isSuper && (
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label={t('admin.clips.uploadDialog.federationLabel')} htmlFor="federation_id">
                <select
                  id="federation_id"
                  value={federationId}
                  onChange={(e) => setFederationId(e.target.value)}
                  className="h-10 w-full rounded-sm border border-line-2 bg-surface px-3 text-sm text-ink outline-none focus:border-ink-3"
                >
                  <option value="">{t('admin.clips.uploadDialog.chooseEllipsis')}</option>
                  {/* Official = a platform clip (federation_id NULL), usable in Official clip exams. */}
                  <option value={OFFICIAL_VALUE}>{t('admin.clips.uploadDialog.officialAllFederations')}</option>
                  {(federations.data ?? []).map((f) => (
                    <option key={f.id} value={f.id}>
                      {f.name}
                    </option>
                  ))}
                </select>
              </Field>

              <Field label={t('admin.clips.uploadDialog.sportLabel')} htmlFor="sport_id">
                <select
                  id="sport_id"
                  value={sportId}
                  onChange={(e) => setSportId(e.target.value)}
                  className="h-10 w-full rounded-sm border border-line-2 bg-surface px-3 text-sm text-ink outline-none focus:border-ink-3"
                >
                  <option value="">{t('admin.clips.uploadDialog.chooseEllipsis')}</option>
                  {(sports.data ?? []).map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>
              </Field>
            </div>
          )}

          <Field
            label={t('admin.clips.uploadDialog.fileLabel', { maxMin: MAX_CLIP_MIN, maxMb: MAX_CLIP_MB })}
            htmlFor="file"
          >
            <input
              id="file"
              type="file"
              accept="video/mp4"
              onChange={(e) => void selectFile(e.currentTarget.files?.[0] ?? null)}
              className="block w-full text-sm text-ink-2 file:mr-3 file:rounded-sm file:border-0 file:bg-accent-tint file:px-3 file:py-2 file:text-[13px] file:font-medium file:text-accent hover:file:opacity-80"
            />
            {file ? (
              <>
                <div className="mt-1 text-[11.5px] text-muted">
                  {t('admin.clips.uploadDialog.fileSummary', {
                    name: file.name,
                    sizeMb: (file.size / 1024 / 1024).toFixed(2),
                  })}
                  {probing && t('admin.clips.uploadDialog.reading')}
                  {probe?.durationSec != null && ` · ${formatDuration(probe.durationSec)}`}
                </div>
                {fileProblem && <div className="mt-1 text-[11.5px] text-danger">{fileProblem}</div>}
              </>
            ) : (
              <div className="mt-1 text-[11.5px] text-muted">
                {t('admin.clips.uploadDialog.namedAfterFile')}
              </div>
            )}
          </Field>
        </div>
      )}

      {stage === 'working' && (
        <WorkingPanel isUploading={isUploading} serverStatus={serverStatus} uploaded={uploaded} fileName={clip?.title ?? file?.name ?? ''} />
      )}

      {stage === 'failed' && (
        <div className="flex items-start gap-3 rounded-sm border border-danger/40 bg-danger/10 p-4">
          <Icon.close className="mt-0.5 h-4 w-4 flex-none text-danger" />
          <div className="text-[13px] text-danger">{t('admin.clips.uploadDialog.failedBody')}</div>
        </div>
      )}
    </Modal>
  );
}

// The processing view. Sub-status is derived: while the PUT streams it's "Uploading"; once the
// bytes land we're waiting on MediaConvert ("Transcoding"). Always offers the reassurance that the
// admin can walk away — the whole point of the redesign.
function WorkingPanel({
  isUploading,
  serverStatus,
  uploaded,
  fileName,
}: {
  isUploading: boolean;
  serverStatus: string | undefined;
  uploaded: boolean;
  fileName: string;
}) {
  const { t } = useTranslation();
  const label = isUploading
    ? t('admin.clips.uploadDialog.uploadingLabel')
    : serverStatus === 'processing'
      ? t('admin.clips.uploadDialog.transcodingLabel')
      : t('admin.clips.uploadDialog.processingLabel');

  return (
    <div className="flex flex-col items-center gap-4 py-4 text-center">
      <div className="grid h-14 w-14 place-items-center rounded-full bg-accent-tint text-accent">
        <Spinner className="h-6 w-6" />
      </div>
      <div>
        <div className="text-[14px] font-semibold text-ink">{label}</div>
        {fileName && <div className="mt-1 max-w-[320px] truncate text-[12px] text-muted">{fileName}</div>}
      </div>
      {/* Only offer the "you can leave" reassurance once the bytes are safely in S3 — before that,
          leaving would abort the upload, so we don't invite it. */}
      {uploaded && (
        <p className="max-w-[360px] text-[12px] leading-relaxed text-muted">
          {t('admin.clips.uploadDialog.canLeaveNote')}
        </p>
      )}
    </div>
  );
}

// Footer swaps with the stage: pick-a-file → Cancel + Upload; working → a spinner then Leave;
// failed → Close + Try again.
function Footer({
  stage,
  isUploading,
  canUpload,
  onUpload,
  onLeave,
  onRetry,
}: {
  stage: 'select' | 'working' | 'failed';
  isUploading: boolean;
  canUpload: boolean;
  onUpload: () => void;
  onLeave: () => void;
  onRetry: () => void;
}) {
  const { t } = useTranslation();
  if (stage === 'failed') {
    return (
      <>
        <Button onClick={onLeave}>{t('admin.clips.uploadDialog.close')}</Button>
        <Button variant="accent" onClick={onRetry}>
          {t('admin.clips.uploadDialog.tryAgain')}
        </Button>
      </>
    );
  }

  if (stage === 'working') {
    // While the PUT streams there's nothing to do but wait (dismiss is blocked). Once uploaded, the
    // admin can leave and let transcoding finish in the background.
    return isUploading ? (
      <Button variant="accent" disabled>
        <Spinner className="h-3.5 w-3.5" />
        {t('admin.clips.uploadDialog.uploading')}
      </Button>
    ) : (
      <Button onClick={onLeave}>{t('admin.clips.uploadDialog.leaveInBackground')}</Button>
    );
  }

  return (
    <>
      <Button onClick={onLeave}>{t('common.cancel')}</Button>
      <Button variant="accent" onClick={onUpload} disabled={!canUpload}>
        {t('admin.clips.uploadDialog.uploadButton')} <Icon.upload className="h-3.5 w-3.5" />
      </Button>
    </>
  );
}
